"use client";

import { useEffect, useRef, useState } from "react";
import { FiBell } from "react-icons/fi";
import CdnImage from "@/components/CdnImage";
import TradingPanel from "@/app/market/[slug]/components/TradingPanel";
import { MarketPriceProvider } from "@/app/market/[slug]/context/MarketPriceContext";
import type { Market, Source, GetLivePriceFn, GetLiveBuyPricesFn, OpenTradingModalFn } from "./types";
import { formatPriceCents } from "./types";
import { useT } from "@/hooks/useT";

interface MobileBottomSheetProps {
  selectedMarket: Market | null;
  activeMarketsCount: number;
  source: Source;
  mobileTradeOpen: boolean;
  setMobileTradeOpen: (v: boolean) => void;
  setAlertModalOpen: (v: boolean) => void;
  getLivePrice: GetLivePriceFn;
  getLiveBuyPrices: GetLiveBuyPricesFn;
  openTradingModal: OpenTradingModalFn;
  eventClosed?: boolean;
  /** Outcome the user picked on the grid row (0 = YES, 1 = NO). Forwarded
   *  to TradingPanel so the inline Polymarket panel opens pre-selected on
   *  the side the user just tapped instead of always defaulting to YES. */
  initialOutcomeIndex?: 0 | 1 | null;
  /** Other sub-markets in this event — enables the horizontal switcher
   *  that lets the user flip markets without closing the sheet. */
  activeMarkets?: Market[];
  /** Called when the user taps another market pill inside the sheet. */
  onSelectMarket?: (id: string) => void;
}

export default function MobileBottomSheet({
  selectedMarket,
  activeMarketsCount,
  source,
  mobileTradeOpen,
  setMobileTradeOpen,
  setAlertModalOpen,
  getLivePrice,
  getLiveBuyPrices,
  openTradingModal,
  eventClosed,
  initialOutcomeIndex,
  activeMarkets,
  onSelectMarket,
}: MobileBottomSheetProps) {
  const t = useT();
  const [marketDropdownOpen, setMarketDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!marketDropdownOpen) return;
    const onClickOutside = (e: Event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setMarketDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("touchstart", onClickOutside);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("touchstart", onClickOutside);
    };
  }, [marketDropdownOpen]);
  if (!selectedMarket) return null;
  const selectedMarketImage = selectedMarket.imageUrl || selectedMarket.image || undefined;

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[200]">
      {/* Backdrop — also show for closed events so user can tap outside to close */}
      {mobileTradeOpen && (activeMarketsCount > 1 || eventClosed) && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileTradeOpen(false)} />
      )}

      {/* Bottom sheet — binary markets show buttons directly, multi-outcome has expandable sheet */}
      {activeMarketsCount === 1 ? (
        <div className="bg-white dark:bg-[#18181D] border-t border-gray-200 dark:border-[#1A1A1A] shadow-[0_-4px_20px_rgba(0,0,0,0.15)] px-4 py-3">
          <div className="flex gap-2">
            <button
              onClick={() => {
                const { buyYes } = getLiveBuyPrices(selectedMarket);
                openTradingModal({
                  isOpen: true, marketSlug: selectedMarket.slug, outcome: "YES", outcomePrice: buyYes,
                  marketQuestion: selectedMarket.question, actualOutcome: selectedMarket.outcomes?.[0], outcomes: selectedMarket.outcomes,
                });
              }}
              disabled={!selectedMarket.active || !selectedMarket.acceptingOrders}
              className={`flex-1 py-3 rounded-[5px] font-inter-tight font-black text-sm tabular-nums transition-colors ${
                selectedMarket.active && selectedMarket.acceptingOrders
                  ? "bg-[#22C55E] text-white active:scale-[0.98]"
                  : "bg-gray-200 dark:bg-[#1F2937] text-gray-500 cursor-not-allowed opacity-50"
              }`}
            >
              {(() => { const { buyYes } = getLiveBuyPrices(selectedMarket); return `Yes ${formatPriceCents(buyYes || 0, selectedMarket.decimalPrecision)}¢`; })()}
            </button>
            <button
              onClick={() => {
                const { buyNo } = getLiveBuyPrices(selectedMarket);
                openTradingModal({
                  isOpen: true, marketSlug: selectedMarket.slug, outcome: "NO", outcomePrice: buyNo,
                  marketQuestion: selectedMarket.question, actualOutcome: selectedMarket.outcomes?.[1], outcomes: selectedMarket.outcomes,
                });
              }}
              disabled={!selectedMarket.active || !selectedMarket.acceptingOrders}
              className={`flex-1 py-3 rounded-[5px] font-inter-tight font-black text-sm tabular-nums transition-colors ${
                selectedMarket.active && selectedMarket.acceptingOrders
                  ? "bg-[#EF4444] text-white active:scale-[0.98]"
                  : "bg-gray-200 dark:bg-[#1F2937] text-gray-500 cursor-not-allowed opacity-50"
              }`}
            >
              {(() => { const { buyNo } = getLiveBuyPrices(selectedMarket); return `No ${formatPriceCents(buyNo || 0, selectedMarket.decimalPrecision)}¢`; })()}
            </button>
          </div>
        </div>
      ) : (
        <div className={`relative bg-white dark:bg-[#18181D] border-t border-gray-200 dark:border-[#1A1A1A] shadow-[0_-4px_20px_rgba(0,0,0,0.15)] transition-all duration-300 ease-out ${
          mobileTradeOpen ? 'max-h-[88vh] rounded-t-[5px]' : 'max-h-[64px]'
        } overflow-hidden`}>
          {/* Collapsed/header bar — hidden when event is closed (TradingPanel
              shows Resolution info). When a multi-market dropdown is going to
              render below (the sub-market selector), we drop the image+title
              from this bar so it doesn't repeat the same context twice on a
              ~640px-wide phone — the dropdown becomes the single source of
              truth for "which sub-market" and this bar only carries the
              chrome (alert + close). Single-market events keep the title up
              top because there's no dropdown to anchor it. */}
          {!eventClosed && (() => {
            const hasMarketDropdown = !!(
              mobileTradeOpen &&
              activeMarkets &&
              activeMarkets.length > 1 &&
              onSelectMarket
            );
            return (
            <div
              className="flex items-center gap-3 px-4 py-3 cursor-pointer"
              onClick={() => {
                setMobileTradeOpen(!mobileTradeOpen);
              }}
            >
              {!hasMarketDropdown && selectedMarketImage && (
                <CdnImage
                  src={selectedMarketImage}
                  fallbackSrc={selectedMarket.image || undefined}
                  alt=""
                  width={32}
                  height={32}
                  className="w-8 h-8 rounded-[5px] object-cover flex-shrink-0"
                />
              )}
              {!hasMarketDropdown ? (
                <span className="font-inter-tight font-bold text-sm text-gray-900 dark:text-white truncate flex-1">
                  {selectedMarket.groupItemTitle || selectedMarket.question}
                </span>
              ) : (
                <span className="flex-1" />
              )}
              <button
                onClick={(e) => { e.stopPropagation(); setAlertModalOpen(true); }}
                className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-[5px] bg-amber-500 hover:bg-amber-600 text-white transition-colors"
                title={t("home.visual.track.priceAlert")}
              >
                <FiBell className="w-3.5 h-3.5" strokeWidth={2.4} />
              </button>
              {mobileTradeOpen && (
                <button onClick={(e) => { e.stopPropagation(); setMobileTradeOpen(false); }} className="flex-shrink-0 p-1 rounded-[5px] hover:bg-gray-100 dark:hover:bg-[#222] transition-colors">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
                </button>
              )}
            </div>
            );
          })()}

          {/* Expanded content */}
          {mobileTradeOpen && (
            <div className="overflow-y-auto max-h-[calc(88vh-64px)] px-4 pb-6">
              {/* Sub-market dropdown selector — mirror of the desktop
                  dropdown that sits above PlaceTrade. Tap to open the
                  list, tap an item to switch markets without closing
                  the sheet. */}
              {activeMarkets && activeMarkets.length > 1 && onSelectMarket && !eventClosed && (
                <div ref={dropdownRef} className="relative mb-3">
                  <button
                    onClick={() => setMarketDropdownOpen((v) => !v)}
                    className="w-full flex items-center gap-2 h-11 px-2.5 rounded-[5px] bg-gray-50 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] text-left transition-colors"
                  >
                    {(selectedMarket.imageUrl || selectedMarket.image) && (
                      <CdnImage
                        src={selectedMarket.imageUrl || selectedMarket.image || ""}
                        alt=""
                        width={24}
                        height={24}
                        className="w-6 h-6 rounded-[5px] object-cover flex-shrink-0"
                      />
                    )}
                    <span className="flex-1 text-[13px] font-semibold text-gray-900 dark:text-white truncate">
                      {selectedMarket.groupItemTitle || selectedMarket.question}
                    </span>
                    <span className="text-[12px] font-bold tabular-nums text-gray-500 dark:text-gray-400 shrink-0">
                      {formatPriceCents(getLiveBuyPrices(selectedMarket).buyYes || 0, selectedMarket.decimalPrecision)}%
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${marketDropdownOpen ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.4}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {marketDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-lg max-h-[280px] overflow-y-auto">
                      {activeMarkets
                        .filter((m) => m.id !== selectedMarket.id)
                        .map((m) => {
                          const { buyYes } = getLiveBuyPrices(m);
                          const mImg = m.imageUrl || m.image || selectedMarket.image || undefined;
                          return (
                            <button
                              key={m.id}
                              onClick={() => {
                                onSelectMarket(m.id);
                                setMarketDropdownOpen(false);
                              }}
                              className="w-full flex items-center gap-2 px-2.5 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-[#0f0f12] border-b border-gray-100 dark:border-[#1A1A1A] last:border-b-0"
                            >
                              {mImg && (
                                <CdnImage
                                  src={mImg}
                                  alt=""
                                  width={22}
                                  height={22}
                                  className="w-5.5 h-5.5 rounded-[5px] object-cover flex-shrink-0"
                                />
                              )}
                              <span className="flex-1 text-[12px] font-medium text-gray-700 dark:text-gray-300 truncate">
                                {m.groupItemTitle || m.question}
                              </span>
                              <span className="text-[12px] font-bold tabular-nums text-gray-500 dark:text-gray-400 shrink-0">
                                {formatPriceCents(buyYes || 0, m.decimalPrecision)}%
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}

              {source === 'polymarket' ? (
                <MarketPriceProvider
                  clobTokenIds={selectedMarket.clobTokenIds || []}
                  initialPrices={(() => {
                    const { buyYes, buyNo } = getLiveBuyPrices(selectedMarket);
                    return [buyYes, buyNo];
                  })()}
                  realtimeUpdates
                >
                  <TradingPanel
                    marketData={{
                      slug: selectedMarket.slug,
                      question: selectedMarket.question,
                      outcomePrices: selectedMarket.outcomePrices,
                      outcomes: selectedMarket.outcomes,
                      clobTokenIds: selectedMarket.clobTokenIds,
                      image: selectedMarket.image,
                      groupItemTitle: selectedMarket.groupItemTitle,
                    }}
                    isActive={selectedMarket.active && selectedMarket.acceptingOrders && !selectedMarket.closed}
                    initialOutcomeIndex={initialOutcomeIndex}
                    conditionId={selectedMarket.conditionId}
                    compact
                  />
                </MarketPriceProvider>
              ) : (
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => {
                      const [yesPrice] = getLivePrice(selectedMarket);
                      openTradingModal({
                        isOpen: true, marketSlug: selectedMarket.slug, outcome: "YES", outcomePrice: yesPrice,
                        marketQuestion: selectedMarket.question, actualOutcome: selectedMarket.outcomes?.[0], outcomes: selectedMarket.outcomes,
                      });
                      setMobileTradeOpen(false);
                    }}
                    className="flex-1 py-3 rounded-[5px] font-inter-tight font-black text-sm bg-[#22C55E] text-white"
                  >
                    {(() => { const { buyYes } = getLiveBuyPrices(selectedMarket); return `${typeof selectedMarket.outcomes?.[0] === 'string' ? selectedMarket.outcomes[0] : (selectedMarket.outcomes?.[0] as any)?.name || 'Yes'} ${formatPriceCents(buyYes || 0, selectedMarket.decimalPrecision)}¢`; })()}
                  </button>
                  <button
                    onClick={() => {
                      const { buyNo } = getLiveBuyPrices(selectedMarket);
                      openTradingModal({
                        isOpen: true, marketSlug: selectedMarket.slug, outcome: "NO", outcomePrice: buyNo,
                        marketQuestion: selectedMarket.question, actualOutcome: selectedMarket.outcomes?.[1], outcomes: selectedMarket.outcomes,
                      });
                      setMobileTradeOpen(false);
                    }}
                    className="flex-1 py-3 rounded-[5px] font-inter-tight font-black text-sm bg-[#EF4444] text-white"
                  >
                    {(() => { const { buyNo } = getLiveBuyPrices(selectedMarket); return `${typeof selectedMarket.outcomes?.[1] === 'string' ? selectedMarket.outcomes[1] : (selectedMarket.outcomes?.[1] as any)?.name || 'No'} ${formatPriceCents(buyNo || 0, selectedMarket.decimalPrecision)}¢`; })()}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
